import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertCircle, RotateCcw, Home, Copy, Check } from 'lucide-react';

interface Props {
  children?: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  copied: boolean;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
    copied: false,
  };

  public static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[ErrorBoundary] Uncaught error:', error, errorInfo);
    this.setState({ errorInfo });

    // Auto-report to console with structured data for debugging
    const report = {
      timestamp: new Date().toISOString(),
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      url: window.location.href,
      userAgent: navigator.userAgent,
    };
    console.error('[ErrorBoundary] Report:', JSON.stringify(report, null, 2));
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.href = '/';
  };

  private handleCopyError = () => {
    const { error, errorInfo } = this.state;
    const text = [
      `Error: ${error?.message}`,
      `URL: ${window.location.href}`,
      `Time: ${new Date().toISOString()}`,
      '',
      error?.stack?.split('\n').slice(0, 5).join('\n'),
      '',
      'Component Stack:',
      errorInfo?.componentStack?.split('\n').slice(0, 5).join('\n'),
    ].join('\n');

    navigator.clipboard.writeText(text).then(() => {
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 2000);
    });
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      return (
        <div className="min-h-screen bg-slate-50 dark:bg-neutral-950 flex flex-col items-center justify-center p-6 text-center transition-colors">
          {/* Icon */}
          <div className="w-20 h-20 bg-rose-50 dark:bg-rose-950/30 rounded-sm flex items-center justify-center text-rose-500 mb-6 shadow-xs border border-rose-100 dark:border-rose-900/50">
            <AlertCircle size={40} />
          </div>

          <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">Something went wrong</h1>

          <p className="text-slate-500 dark:text-neutral-400 max-w-md mb-8 text-sm leading-relaxed">
            An unexpected error occurred. Please refresh or return to the home page.
          </p>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 w-full max-w-xs">
            <button
              onClick={() => window.location.reload()}
              className="flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-sm font-bold transition-all shadow-lg shadow-blue-600/20 active:scale-95"
            >
              <RotateCcw size={18} />
              Refresh Page
            </button>
            <button
              onClick={this.handleReset}
              className="flex items-center justify-center gap-2 px-6 py-3 bg-white dark:bg-neutral-900 text-slate-600 dark:text-neutral-300 border border-slate-200 dark:border-neutral-800 rounded-sm font-bold hover:bg-slate-50 dark:hover:bg-neutral-800 transition-all active:scale-95"
            >
              <Home size={16} />
              Home
            </button>
          </div>

          {/* Technical Details */}
          <div className="mt-12 p-4 bg-slate-100 dark:bg-neutral-900/50 rounded-sm border border-slate-200 dark:border-neutral-800 w-full max-w-xl text-left">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] font-mono text-slate-400 uppercase tracking-widest px-1">Technical Details</p>
              <button
                onClick={this.handleCopyError}
                className="flex items-center gap-1 text-[10px] font-bold text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 px-2 py-1 rounded-sm hover:bg-slate-200 dark:hover:bg-neutral-800 transition-colors"
              >
                {this.state.copied ? (
                  <>
                    <Check size={12} className="text-green-500" /> Copied
                  </>
                ) : (
                  <>
                    <Copy size={12} /> Copy
                  </>
                )}
              </button>
            </div>
            <div className="max-h-32 overflow-auto text-[11px] font-mono text-rose-500 dark:text-rose-400 bg-black/5 dark:bg-black/20 p-3 rounded-sm">
              {this.state.error?.message || 'Unknown error'}
              <br />
              {this.state.error?.stack?.split('\n').slice(0, 3).join('\n')}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
